import { QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Outlet, Route, Routes } from 'react-router-dom';

import { BuildDriftBanner } from './components/BuildDriftBanner.tsx';
import { startClog } from './lib/clog.ts';
import { queryClient } from './lib/query-client.ts';
import { WidgetsProvider } from './lib/widgets-provider.tsx';
import './styles.css';
import './snapshot/tokens.css';
import { Connected } from './pages/Connected.tsx';
import { ConnectRepo } from './pages/ConnectRepo.tsx';
import { DataSourcesIntegrations } from './pages/DataSourcesIntegrations.tsx';
import { DataSourcesManageOrgs } from './pages/DataSourcesManageOrgs.tsx';
import { Landing } from './pages/Landing.tsx';
import { Layout } from './pages/Layout.tsx';
import { Login } from './pages/Login.tsx';
import { NarrativeTest } from './pages/narrative/NarrativeTest.tsx';
import { RequireAuth } from './pages/RequireAuth.tsx';
import { SettingsContent } from './pages/Settings.tsx';
import { SettingsEngineers } from './pages/SettingsEngineers.tsx';
import { SettingsLinkRepo } from './pages/SettingsLinkRepo.tsx';
import { SettingsProfile } from './pages/SettingsProfile.tsx';
import { SettingsSetup } from './pages/SettingsSetup.tsx';
import { SettingsTeam } from './pages/SettingsTeam.tsx';
import { SetupPlatformMap } from './pages/SetupPlatformMap.tsx';
import { OnboardingBanner } from './snapshot/components/OnboardingBanner.tsx';
import { TooltipProvider } from './snapshot/components/Tooltip.tsx';
import { AccountingOverview } from './snapshot/views/accounting/AccountingOverview.tsx';
import { AccountingShell } from './snapshot/views/accounting/AccountingShell.tsx';
import { CommitAuditView } from './snapshot/views/accounting/CommitAuditView.tsx';
import { EngineerDetailView } from './snapshot/views/accounting/EngineerDetailView.tsx';
import { ActivityView } from './snapshot/views/ActivityView.tsx';
import { ResilienceView } from './snapshot/views/ResilienceView.tsx';
import { RoadmapView } from './snapshot/views/RoadmapView.tsx';
import { TalentView } from './snapshot/views/TalentView.tsx';
import { TimelineDeckView } from './snapshot/views/TimelineDeckView.tsx';
import { TimelineView } from './snapshot/views/TimelineView.tsx';

startClog();

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element not found');

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <WidgetsProvider>
        <BrowserRouter>
          <BuildDriftBanner />
          <Routes>
            <Route path='/' element={<Landing />} />
            <Route path='/sign-in' element={<Login />} />
            {/* Presentational PDF-export canvas. Outside RequireAuth + Layout so
                the headless export (Chromium page.pdf) renders a clean deck from
                window.__DECK_DATA__ with no session/nav. Not linked from any nav. */}
            <Route path='/timeline/deck' element={<TimelineDeckView />} />
            {/* Everything below requires a session; signed-out visitors are
                redirected to the WorkOS hosted sign-up screen. */}
            <Route element={<RequireAuth />}>
              {/* Standalone, unlinked client-validation page. Sits inside
                  RequireAuth (uses the current session) but outside Layout,
                  so it renders as a clean full-screen canvas with no nav
                  rail. Not referenced from any nav. */}
              <Route path='/narrative-test' element={<NarrativeTest />} />
              <Route path='/onboarding/connect-repo' element={<ConnectRepo />} />
              <Route path='/onboarding/connected' element={<Connected />} />
              {/* Single app shell. Layout stays mounted across every route
                  below, so its rail (and the expand/collapse state of the
                  settings nav) persists as the user moves between views and
                  settings. Settings is no longer a separate layout: on a
                  /settings or /setup route Layout widens the rail and shows
                  the settings nav in place. */}
              <Route element={<Layout />}>
                <Route
                  element={
                    <TooltipProvider>
                      <OnboardingBanner />
                      <Outlet />
                    </TooltipProvider>
                  }
                >
                  <Route path='/resilience' element={<ResilienceView />} />
                  <Route path='/talent' element={<TalentView />} />
                  <Route path='/timeline' element={<TimelineView />} />
                  <Route path='/roadmap' element={<RoadmapView />} />
                  <Route path='/accounting' element={<AccountingShell />}>
                    <Route index element={<AccountingOverview />} />
                    <Route path='engineer/:engineerId' element={<EngineerDetailView />} />
                    <Route path='commit/:sha' element={<CommitAuditView />} />
                  </Route>
                  <Route path='/activity' element={<ActivityView />} />
                </Route>
                {/* Account pages — reached from the org switcher menu. */}
                <Route path='/settings' element={<SettingsContent />}>
                  <Route index element={<SettingsProfile />} />
                  <Route path='profile' element={<SettingsProfile />} />
                  <Route path='team' element={<SettingsTeam />} />
                  <Route path='setup' element={<SettingsSetup />} />
                </Route>
                {/* Org configuration — reached from the gear in the rail.
                    Merges the former Setup + Data sources areas:
                    integrations / manage-orgs / platform-map / engineers,
                    plus Link New Repo (jumps to connect-repo onboarding). */}
                <Route path='/setup' element={<SettingsContent />}>
                  <Route index element={<SetupPlatformMap />} />
                  <Route path='integrations' element={<DataSourcesIntegrations />} />
                  <Route path='manage-orgs' element={<DataSourcesManageOrgs />} />
                  <Route path='platform-map' element={<SetupPlatformMap />} />
                  <Route path='engineers' element={<SettingsEngineers />} />
                  <Route path='link-repo' element={<SettingsLinkRepo />} />
                </Route>
              </Route>
            </Route>
          </Routes>
        </BrowserRouter>
      </WidgetsProvider>
    </QueryClientProvider>
  </StrictMode>
);
